// Shared via top-level window so both editor and canvas iframe bundles
// reference the same object (Parcel creates separate module instances,
// and iframe has its own window).
// Walk up to the topmost same-origin ancestor so cross-origin embed
// (landing → editor → canvas) still shares scope between editor chrome
// and canvas iframe.
// Walk to the topmost same-origin ancestor window. Exported so other
// modules (state.js, methods/index.js) don't redefine it.
export const resolveTopWindow = () => {
  if (typeof window === 'undefined') return globalThis
  let w = window
  while (w.parent && w.parent !== w) {
    try { void w.parent.document; w = w.parent } catch { break }
  }
  return w
}
const topWindow = resolveTopWindow()

// Canvas mode constants — single source for both editor chrome and
// canvas iframe so we don't drift on string typos.
export const MODES = Object.freeze({ DEFAULT: 'default', PAN: 'pan', INSPECT: 'inspect', INTERACT: 'interact' })
export const HANDLE_NAMES = Object.freeze([
  'CvHandleNW', 'CvHandleN', 'CvHandleNE', 'CvHandleE',
  'CvHandleSE', 'CvHandleS', 'CvHandleSW', 'CvHandleW'
])

// Live v2 viewport mirror — written by state.js applyViewport, read by
// the panzoom shim below so the outer chrome's animation-frame loop
// always sees fresh values without waiting for component init.
if (typeof topWindow !== 'undefined' && !topWindow.__cvViewport) {
  topWindow.__cvViewport = { x: 40, y: 40, scale: 0.35 }
}

if (!topWindow.__canvasGlobalScope) {
  topWindow.__canvasGlobalScope = {
    // Panzoom (v1)
    panningMode: false,
    isPanning: false,
    isGrabbing: false,
    positions: { x: 0, y: 0, scale: 0.35 },
    panzoomInstance: null,

    // Selection state (element refs — NOT stored in reactive store)
    selected: null,
    selectedKey: null,
    hovered: null,
    hoveredKey: null,

    canvasElement: null,

    // Modes (v1)
    defaultMode: true,
    inspectMode: false,
    editorActive: true,

    // Inspect layers
    hoverLayer: null,
    activeLayer: null,
    layerHostComponent: null,
    inContext: null,

    _syncCounter: 0,

    // Canvas v2 feature flag — v2 state itself lives on reactive root
    // state (see functions/canvas/state.js). This scope only carries the
    // flag because it's read by both editor chrome and iframe.
    // Canvas v2 is now the default. Legacy gridstack canvas (v1) is
    // opt-in via `?canvasOld=true` or `?canvasV2=0`; also kept
    // overridable via localStorage `canvasOld` for stable bookmarks.
    canvasV2: (() => {
      try {
        if (typeof window === 'undefined') return true
        const sp = new URLSearchParams(window.location.search)
        if (sp.get('canvasV2') === '1') return true
        if (sp.get('canvasV2') === '0') return false
        if (sp.get('canvasOld') === 'true' || sp.get('canvasOld') === '1') return false
        if (window.localStorage?.getItem('canvasOld')) return false
        return true
      } catch { return true }
    })()
  }
}

// Install the v1-shaped panzoom shim eagerly when v2 is on so the
// shared Zoombar's per-frame loop always has something to read. The
// shim reads `topWindow.__cvViewport`, which `applyViewport` keeps
// fresh.
if (topWindow.__canvasGlobalScope.canvasV2 && !topWindow.__canvasGlobalScope.panzoomInstance?.__v2) {
  const vp = () => topWindow.__cvViewport || { x: 40, y: 40, scale: 0.35 }
  topWindow.__canvasGlobalScope.panzoomInstance = {
    __v2: true,
    getTransform: () => ({ ...vp() }),
    moveTo: (x, y) => { topWindow.__cvViewport = { ...vp(), x, y } },
    moveBy: (dx, dy) => { const v = vp(); topWindow.__cvViewport = { ...v, x: v.x + dx, y: v.y + dy } },
    zoomAbs: (_x, _y, scale) => { topWindow.__cvViewport = { ...vp(), scale } },
    smoothZoom: (cx, cy, factor) => {
      const v = vp(); const ns = Math.max(0.035, Math.min(2.5, v.scale * factor))
      topWindow.__cvViewport = { x: cx - (cx - v.x) * (ns / v.scale), y: cy - (cy - v.y) * (ns / v.scale), scale: ns }
    },
    smoothZoomAbs: (_x, _y, scale) => { topWindow.__cvViewport = { ...vp(), scale } },
    on: () => {}, off: () => {}, dispose: () => {}
  }
}

export default topWindow.__canvasGlobalScope
